/**
 * Módulo de manejo monetario.
 *
 * Todas las operaciones monetarias del flujo de reserva pasan por aquí.
 * Se opera en centavos (enteros) para evitar errores de coma flotante como
 * `0.1 + 0.2 === 0.30000000000000004`, que en producción se traducen en
 * montos guardados como 9 cuando el usuario tipeó 10.
 *
 * Convención:
 *   - Toda entrada externa (string del input, número del backend) se
 *     normaliza con `toCents` antes de operar.
 *   - Toda salida hacia el UI o el backend pasa por `fromCents` o
 *     `formatAmount` para garantizar 2 decimales fijos.
 */

export const MONEY_EPSILON = 0.01;

/**
 * Convierte un valor (string o number) a centavos enteros.
 * Cualquier entrada inválida resuelve a 0.
 *
 * @param {string|number|null|undefined} value
 * @returns {number} entero >= 0
 */
export const toCents = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(num) || !Number.isFinite(num)) return 0;
  // Round to avoid 0.1+0.2 type drift before truncation.
  return Math.round(num * 100);
};

/**
 * Convierte centavos a número decimal con 2 dígitos.
 *
 * @param {number} cents
 * @returns {number}
 */
export const fromCents = (cents) => {
  const safe = Number.isFinite(cents) ? cents : 0;
  return Math.round(safe) / 100;
};

/**
 * Formatea centavos como string con 2 decimales, listo para mostrar o enviar.
 * Nunca produce notación científica ni decimales colgantes.
 *
 * @param {number} cents
 * @returns {string} ej. "10.00"
 */
export const formatAmount = (cents) => fromCents(cents).toFixed(2);

/**
 * Resta segura en centavos, nunca negativa.
 *
 * @param {number} aCents
 * @param {number} bCents
 * @returns {number} max(0, a-b)
 */
export const subtractCents = (aCents, bCents) => Math.max(0, aCents - bCents);

/**
 * Suma una lista de montos (números o strings) y devuelve el total en centavos.
 *
 * @param {Array<string|number>} values
 * @returns {number}
 */
export const sumCents = (values) =>
  values.reduce((acc, v) => acc + toCents(v), 0);

/**
 * Compara dos montos en centavos con tolerancia de 1 centavo.
 *
 * @param {number} aCents
 * @param {number} bCents
 * @returns {boolean}
 */
export const equalsAmount = (aCents, bCents) => Math.abs(aCents - bCents) <= 1;

/**
 * Aplica un código promocional a un monto base.
 *
 * @param {number} amountCents - monto base en centavos
 * @param {{ discountType: 'percentage' | 'fixed', discountValue: number } | null} promo
 * @returns {number} descuento en centavos
 */
export const calculateDiscountCents = (amountCents, promo) => {
  if (!promo) return 0;
  if (promo.discountType === 'percentage') {
    // Math.round + division by 100 to keep integer cents and avoid float drift.
    return Math.round((amountCents * Number(promo.discountValue)) / 100);
  }
  if (promo.discountType === 'fixed') {
    return toCents(promo.discountValue);
  }
  return 0;
};

/**
 * Convierte un monto a moneda extranjera usando una tasa de cambio decimal.
 *
 * @param {number} amountCents
 * @param {number} rate - ej. 36.5 (1 USD = 36.5 VES)
 * @returns {string} formateado a 2 decimales en la moneda extranjera
 */
export const convertToForeignCurrency = (amountCents, rate) => {
  const safeRate = Number.isFinite(Number(rate)) ? Number(rate) : 1;
  // Operamos en decimal aquí porque la tasa puede tener muchos decimales
  // (no son centavos USD, son una unidad distinta).
  const result = fromCents(amountCents) * safeRate;
  return result.toFixed(2);
};
